import * as Leaflet from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-rotatedmarker';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  distinctUntilChanged,
  Observable,
  of,
  skip,
  Subject,
} from 'rxjs';
import { FullScreenMapComponent } from './full-screen-map/full-screen-map.component';
import {
  AutoZoomFitParams,
  CircleShapeOptions,
  Coordinates,
  CustomTemplateProps,
  FullScreenMapProps,
  MapLayer,
  MapMarker,
  MapInput,
  MapShape,
  MarkerUpdateTriggerEvent,
  PolygonShapeOptions,
  LayerEditEvent,
} from './interfaces';
import { MapOptionsService } from './services/map-options.service';
import { MarkerInShapesService } from './services/marker-in-shapes.service';
import { MarkersService } from './services/markers-service';
import { PolyLinesService } from './services/polylines-service';
import { ShapesService } from './services/shapes.service';
import { DialogService } from '@core/services/dialog.service';
import { MapShapeType } from './constants/constants';
import { MapTripImportService } from './services/map-trip-import.service';
import { DvTooltipDirective } from '@devinco/ui/dds';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  providers: [
    MarkersService,
    MapOptionsService,
    ShapesService,
    PolyLinesService,
    MapTripImportService,
    DvTooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MapComponent
  implements OnChanges, OnInit, OnDestroy, AfterViewInit
{
  private dialog = inject(DialogService);
  private mapOptionsService = inject(MapOptionsService);
  private markersService = inject(MarkersService);
  private markerInShapeService = inject(MarkerInShapesService);
  private shapesService = inject(ShapesService);
  private polyLinesService = inject(PolyLinesService);
  private tripImportService = inject(MapTripImportService);

  @Input({ required: true }) id: string;
  /** arrays of points for drawing multiple routes. For to draw route - isRouteInput = true needed
   * (default = false), otherwise marker icons will be placed to each coordinate.
   */
  markers = input<MapInput[] | null>();
  @Input() zoom: number | undefined;
  @Input() showRouteMarkers = false;
  /** polygon or circle */
  @Input() shapes$? = new BehaviorSubject<MapShape[] | undefined>(undefined);
  /** deletes by Id that's defined in circle/polygon options */
  @Input() deleteShapeTrigger$?: Subject<number | string>;
  @Input() invalidateSize$?: Observable<unknown>;
  @Input() clearMapData$?: Observable<unknown>;
  /** auto bounds fit trigger (fits polylines and markers bounds after true is emitted) */
  @Input() autoZoomFit$?: BehaviorSubject<AutoZoomFitParams>;
  @Input() markerUpdateTrigger$?: Subject<MarkerUpdateTriggerEvent>;
  @Input() shapeDrawTrigger$?: Subject<MapShape>;
  /** emit list of shape ids that contains the right click lat/lng */
  @Input() shapeRightClickTrigger$?: Subject<(number | string)[]>;
  /** turn on/off marker clustering. Used only with  useMarkerClustering = true */
  @Input() toggleMarkerClustering$?: BehaviorSubject<boolean>;

  /** Enable drawing in the map (drawing supported only for 1 polygon or circle) */
  enableShapeDrawing = input<boolean>(false);
  isFullScreenModal = input<boolean>(false);
  customTemplate = input<CustomTemplateProps>();
  /** only first editable marker will be overridden with new marker. Used when resetAllMarkersOnClick = false */
  addNewMarkerOnDblClick = input<boolean>();
  center = input<Coordinates>();
  /** arrange markers into clusters. Default = false */
  useMarkerClustering = input<boolean>();
  /** hides full screen button in map toolbar */
  hideFullScreen = input<boolean>();
  /** configuration (if custom) of a marker that gets added to the map on double click */
  newMarkerIconConfig = input<Partial<MapMarker>>();
  /** enable importing trips into map */
  enableTripImport = input<boolean>();
  /** function that gets called when refresh button in the map is clicked */
  mapRefreshFn = input<() => unknown>();
  /** when set to true will enable add new polylines button */
  enablePolylineAdd = input<boolean>();

  closeFullScreen = output<boolean>();
  polylinesEdited = output<LayerEditEvent>();
  polylinePointDeleted = output<LayerEditEvent>();
  polylineAdded = output<LayerEditEvent>();
  markerUpdated = output<MarkerUpdateTriggerEvent>();
  polyEditMarkerClicked = output<LayerEditEvent>();
  mapReady = output<Leaflet.Map>();
  polylineClicked = output<MapLayer>();

  isMapReady = signal<boolean>(false);
  protected options: Leaflet.MapOptions;
  protected mapInstance: Leaflet.Map;

  private defaultBounds: Leaflet.LatLngBounds = Leaflet.latLngBounds(
    Leaflet.latLng(62.2786, 12.3402),
    Leaflet.latLng(62.2786, 12.3402)
  );
  private destroyRef = inject(DestroyRef);
  private newMarkerAlreadyCreated = false;
  private readonly maxZoom = 18;
  private readonly defaultZoom = 4;

  showFullScreenBtn = computed(() => {
    return !this.isFullScreenModal() && !this.hideFullScreen();
  });

  ngOnInit(): void {
    if (this.useMarkerClustering()) {
      this.markersService.markerClusterGroup = Leaflet.markerClusterGroup(
        this.mapOptionsService.getMarkerClusterOptions()
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'markers' in changes &&
      !changes['markers'].isFirstChange() &&
      this.isMapReady() &&
      changes['markers'].previousValue !== changes['markers'].currentValue
    ) {
      this.clearMapData();
      this.initMapAttributes();
    }
  }

  ngAfterViewInit(): void {
    this.onMapReady();
    const customTemplateElement = document.getElementById('map-custom-element');
    if (customTemplateElement) {
      Leaflet.DomEvent.disableClickPropagation(customTemplateElement);
    }
  }

  ngOnDestroy(): void {
    if (this.mapInstance) {
      this.mapInstance.off();
      this.mapInstance.remove();
    }
  }

  initMapAttributes(): void {
    this.initMarkers();

    if (this.shapes$?.getValue()) {
      this.shapesService.initShapes(
        this.mapInstance,
        this.shapes$.getValue() || [],
        this.enableShapeDrawing()
      );
      this.shapesService.updateShapeVisibilityForViewport(this.mapInstance);
    }
    this.fitMarkerLayersBounds();
  }

  openFullScreen(): void {
    const data: FullScreenMapProps = {
      id: this.id + '_full',
      zoom: this.zoom,
      markers$: of(this.markers() || []),
      showRouteMarkers: this.showRouteMarkers,
      customTemplate: this.customTemplate(),
      center: this.center(),
      shapes$: this.shapes$,
    };
    this.dialog.open<FullScreenMapProps, FullScreenMapComponent>({
      size: 'full',
      contentComponent: FullScreenMapComponent,
      hideHeader: true,
      hideFooter: true,
      data,
    });
  }

  onMapReady(): void {
    // setTimeout needed in case the whole app loads on the page with a map
    setTimeout(() => {
      this.mapInstance = Leaflet.map(this.id, {
        zoomControl: false,
        center: this.center(),
        zoom: this.zoom,
        doubleClickZoom: false,
      });

      this.addMapLayersAndControls();
      this.afterMapReady();
      this.shapesService.fixCircleEditRadiusIssue();
      this.mapReady.emit(this.mapInstance);
    });
  }

  onDrawPolygonClicked(): void {
    this.shapesService.triggerDrawPolygon();
  }

  onDrawCircleClicked(): void {
    this.shapesService.triggerDrawCircle();
  }

  /** can be used externally */
  toggleGrayOverlay(action: 'on' | 'off' = 'off'): void {
    if (!this.mapInstance) return;

    if (action === 'on') {
      Leaflet.imageOverlay(
        '/assets/map-overlay.png',
        // globe bounds
        [
          [-90, -180],
          [90, 180],
        ],
        {
          opacity: 0.5,
          interactive: true,
        }
      ).addTo(this.mapInstance);
    } else {
      this.mapInstance.eachLayer(l => {
        if (l instanceof Leaflet.ImageOverlay) {
          this.mapInstance.removeLayer(l);
        }
      });
    }
  }

  close(): void {
    this.clearMapData();
    this.closeFullScreen.emit(true);
  }

  updateGeofenceConnectorPositions(
    positionsByVehicleId: Map<number, Coordinates>
  ): void {
    this.shapesService.updateConnectorPositions(positionsByVehicleId);
  }

  private trigerMarkerUpdateEvent(
    newMarker: MapMarker,
    clickEvent: Leaflet.DragEndEvent | Leaflet.LeafletMouseEvent,
    isNew: boolean
  ): void {
    const event: MarkerUpdateTriggerEvent = {
      ...newMarker,
      isNew,
      isInShape: this.markerInShapeService.isMarkerInsideShape(
        clickEvent,
        Leaflet.marker(newMarker),
        this.shapesService.shapeLayers,
        this.shapes$?.getValue()?.map(shape => shape.type)
      ).isInShape,
    };
    if (this.markerUpdateTrigger$) {
      this.markerUpdateTrigger$.next(event);
    }
    this.markerUpdated.emit(event);
  }

  private initMarkers(markersInput?: MapInput[]): void {
    const markerLayersToAdd = [];

    for (const pointsObj of (markersInput || this.markers()) ?? []) {
      const points = pointsObj.points;
      if (!points?.length) continue;

      const cluster = this.shouldClusterMarkers();

      if (!pointsObj.isRouteInput) {
        const defaultPoints = [];
        const customPoints = [];

        for (const p of points) {
          if (p.options?.customMarker) {
            customPoints.push(p);
          } else {
            defaultPoints.push(p);
          }
        }

        if (defaultPoints.length) {
          this.markersService.drawDefaultMarkers(
            defaultPoints,
            this.mapInstance,
            cluster
          );
        }

        if (customPoints.length) {
          this.markersService.drawCustomMarkers(
            customPoints,
            this.mapInstance,
            cluster
          );
        }
      }

      if (pointsObj.isRouteInput && !pointsObj.hideRouteIcons) {
        const routeMarkers = this.markersService.getRouteStartAndEndMarkers(
          points,
          this.mapInstance,
          this.showRouteMarkers
        );

        for (const m of routeMarkers) {
          markerLayersToAdd.push(m);
        }
      }
    }

    // One push instead of many
    this.markersService.markerLayers.push(...markerLayersToAdd);
    this.polyLinesService.drawMapLines(
      this.markers(),
      this.mapInstance,
      false,
      this.polylinesEdited,
      this.polylinePointDeleted,
      this.enablePolylineAdd() ? this.polylineAdded : undefined,
      this.polyEditMarkerClicked,
      this.polylineClicked
    );

    if (this.shouldClusterMarkers() && this.markersService.markerClusterGroup) {
      this.clearMarkers();
      this.markersService.markerClusterGroup.addTo(this.mapInstance);
    }
    this.setupMarkerDragEvents(this.markersService.markerLayers);
  }

  private setupMarkerDragEvents(markerLayers: MapLayer[]): void {
    markerLayers
      .filter(layer => layer.extraData?.editable)
      .forEach(m => {
        m.on('dragend', event => {
          this.trigerMarkerUpdateEvent(event.target.getLatLng(), event, false);
        });
      });
  }

  private listenToClearMapData(): void {
    this.clearMapData$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearMapData();
      });
  }

  private listenToInvalidateSize(): void {
    this.invalidateSize$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.mapInstance.invalidateSize();
      });
  }

  private listenToShapesInput(): void {
    this.shapes$
      ?.pipe(takeUntilDestroyed(this.destroyRef), skip(1))
      .subscribe(shapes => {
        this.mapInstance.closePopup();
        this.shapesService.deleteShapes(this.mapInstance);
        this.shapesService.initShapes(
          this.mapInstance,
          shapes || [],
          this.enableShapeDrawing()
        );
        this.shapesService.updateShapeVisibilityForViewport(this.mapInstance);
        this.fitMarkerLayersBounds();
      });
  }

  private listenToAutoZoomFit(): void {
    this.autoZoomFit$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(payload => {
        if (payload.doAutoFit && !payload.coordinates) {
          this.fitMarkerLayersBounds();
        } else if (payload.doAutoFit && payload.coordinates) {
          this.mapInstance.fitBounds(
            Leaflet.latLngBounds([
              Leaflet.latLng(payload.coordinates.lat, payload.coordinates.lng),
            ]),
            { maxZoom: this.maxZoom }
          );
        }
      });
  }

  private listenToToggleMarkerClustering(): void {
    this.toggleMarkerClustering$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isOn => {
        if (!this.markersService.markerClusterGroup) return;
        if (isOn) {
          this.clearMarkers();
          // for scenario, when map initially had clustering off and no markers
          if (
            this.markers()?.[0]?.points?.length &&
            this.markersService.markerLayers.length &&
            !this.markersService.markerClusterGroup.getLayers().length
          ) {
            this.initMarkers();
          }
          this.mapInstance.addLayer(this.markersService.markerClusterGroup);
        } else {
          this.mapInstance.removeLayer(this.markersService.markerClusterGroup);
          this.initMarkers();
        }
      });
  }

  private listenToShapeViewportCulling(): void {
    this.mapInstance.on('zoomend moveend', () => {
      this.shapesService.updateShapeVisibilityForViewport(this.mapInstance);
    });
  }

  private listenToDeleteShapeTrigger(): void {
    this.deleteShapeTrigger$
      ?.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(shapeId => {
        let shapeIndex: number | undefined;
        const shapeToDelete = this.shapesService.shapeLayers.find(
          ({ options }, index) => {
            if (
              (options as CircleShapeOptions | PolygonShapeOptions).id ===
              Number(shapeId)
            ) {
              shapeIndex = index;
              return true;
            } else {
              return false;
            }
          }
        );

        this.mapInstance.closePopup();
        if (shapeToDelete && shapeIndex) {
          this.mapInstance.removeLayer(shapeToDelete);
          this.shapesService.shapeLayers.splice(shapeIndex, 1);
        }
      });
  }

  private addMapLayersAndControls(): void {
    this.mapOptionsService.addBaseControls(
      this.mapInstance,
      this.isFullScreenModal()
    );
    if (this.enableTripImport()) {
      this.tripImportService.addTripImportControl(this.mapInstance);
    }
    const mapRefreshFn = this.mapRefreshFn();
    if (mapRefreshFn) {
      this.mapOptionsService.addMapRefreshControl(
        this.mapInstance,
        mapRefreshFn
      );
    }

    this.polyLinesService.setPmDrawTranslations(this.mapInstance);
  }

  private clearMapData(): void {
    this.clearMarkers();
    this.clearMarkerClusterGroup();
    for (const layer of this.shapesService.shapeLayers) {
      this.mapInstance.removeLayer(layer);
    }
    this.markersService.markerLayers = [];
    this.polyLinesService.clearPolylineLayers(this.mapInstance);
    this.shapesService.shapeLayers = [];
  }

  private clearMarkers(): void {
    for (const layer of this.markersService.markerLayers) {
      this.mapInstance.removeLayer(layer);
    }
  }

  private clearMarkerClusterGroup(): void {
    this.markersService.markerClusterGroup?.removeLayers(
      this.markersService.markerClusterGroup.getLayers()
    );
  }

  private boundsFromCenterZoom(
    lat: number,
    lng: number,
    zoom: number,
    map: L.Map
  ): Leaflet.LatLngBounds {
    const size = map.getSize(); // pixels
    const metersPerPixel = 156543.03392 / Math.pow(2, zoom);
    const radius = (metersPerPixel * size.x) / 2;

    return Leaflet.latLng(lat, lng).toBounds(radius);
  }

  private afterMapReady(): void {
    const center = this.center();
    const zoom = this.zoom || this.defaultZoom;
    const bounds = center
      ? this.boundsFromCenterZoom(
          center.lat,
          center.lng,
          zoom,
          this.mapInstance
        )
      : this.defaultBounds;

    this.mapInstance.fitBounds(bounds, { maxZoom: zoom });
    this.initMapAttributes();
    this.fitMarkerLayersBounds();
    this.isMapReady.set(true);
    this.shapesService.listenToShapeDrawEvents(
      this.enableShapeDrawing(),
      this.mapInstance,
      this.shapeDrawTrigger$,
      this.shapes$
    );
    this.listenToClearMapData();
    this.listenToInvalidateSize();
    this.listenToAutoZoomFit();
    this.listenToToggleMarkerClustering();
    this.listenToShapesInput();
    this.listenToDeleteShapeTrigger();
    this.listenToShapeViewportCulling();
    this.listenToMapRightClick();
    this.listenToMapDoubleClick();

    if (this.useMarkerClustering()) {
      this.markersService.createMarkerClusterPopup(this.mapInstance);
    }
  }

  private shouldClusterMarkers(): boolean {
    return (
      !!this.useMarkerClustering() &&
      (this.toggleMarkerClustering$ === undefined ||
        this.toggleMarkerClustering$.getValue())
    );
  }

  private listenToMapRightClick(): void {
    this.mapInstance.on('contextmenu', event => {
      if (this.shapeRightClickTrigger$) {
        const clickMarker = Leaflet.marker(event.latlng);
        const shapes = this.markerInShapeService.isMarkerInsideShape(
          event,
          clickMarker,
          this.shapesService.shapeLayers,
          // filters only correctly designed shapes (if circle then center and radius are mandatory)
          this.shapes$
            ?.getValue()
            ?.filter(shape => {
              if (shape.type === MapShapeType.Circle) {
                return !!shape.center && !!shape.radius;
              }
              return true;
            })
            .map(shape => shape.type)
        ).shapeIds;
        this.shapeRightClickTrigger$.next(shapes);
      }
    });
  }

  private listenToMapDoubleClick(): void {
    if (!this.addNewMarkerOnDblClick()) return;

    this.mapInstance.on('dblclick', event => {
      const newMarkerLatLng = { lat: event.latlng.lat, lng: event.latlng.lng };

      if (this.addNewMarkerOnDblClick() && !this.newMarkerAlreadyCreated) {
        this.newMarkerAlreadyCreated = true;
        const newMarkerConfig = this.newMarkerIconConfig();

        this.initMarkers([
          {
            isRouteInput: false,
            points: [
              {
                ...newMarkerConfig,
                ...newMarkerLatLng,
                extraData: {
                  editable: true,
                  ...newMarkerConfig?.extraData,
                },
              },
            ],
          },
        ]);
        this.trigerMarkerUpdateEvent(newMarkerLatLng, event, true);
      }
    });
  }

  private fitMarkerLayersBounds(): void {
    // this excludes maps that are dependent on entity change in the grid
    if (
      this.isMapReady() &&
      (this.isFullScreenModal() || this.autoZoomFit$) &&
      !this.autoZoomFit$?.getValue().doAutoFit
    )
      return;
    const layers = [
      ...this.markersService.markerLayers,
      ...this.polyLinesService.polyLineLayers,
      ...(this.markersService.markerClusterGroup?.getLayers() || []),
    ];
    // shapes covering the whole viewport (e.g. at high zoom) are culled from the map,
    // and their getBounds() throws without a map, so use map-independent bounds
    const shapeLayers = [...this.shapesService.shapeLayers];

    if (layers.length || shapeLayers.length) {
      const featureGroup = Leaflet.featureGroup(layers);
      setTimeout(() => {
        const bounds = featureGroup.getBounds();
        for (const shape of shapeLayers) {
          bounds.extend(this.shapesService.getShapeGeoBounds(shape));
        }
        if (bounds.isValid()) {
          this.mapInstance.fitBounds(bounds, { maxZoom: this.maxZoom });
        }
      }, 200);
    }
  }
}
